import { getAssetIconBySymbol } from 'abis/set/asset-icons'
import { Flex } from 'components'
import { indexCardStatisticsService } from 'components/cards/index-card-service'
import {
  buildTimeSeriesFromPriceHistory,
  byLastTwoWeeks,
  byXAxis,
} from 'components/charts/atomic-product-chart/services'
import { useTourGuide } from 'context/tour-guide/use-tour-guide'
import { AnalyticsArea, useAnalyticsEventTracker } from 'hooks'
import { PerformanceChartLite } from 'hooks/chart/components/line/product-performance'
import { getProductByAddress } from 'indices-list'
import { ProductOverview } from 'pages/overview/storage/interface'
import React from 'react'
import Path from 'router'
import {
  IconWrapper,
  InvestInProductButton,
  MarketChart,
  MarketDetails,
  PerformanceItem,
  ProductDelta,
  ProductDetails,
  ProductNameAndDescription,
  ProductShortTitle,
  ProductTitle,
  TotalExposureDescription,
} from './style'

export const ProductItem: React.FC<{ product: ProductOverview }> = ({ product }) => {
  const [marketCap, setMarketCap] = React.useState<string>('')
  const [delta, setDelta] = React.useState<string>('')
  const productSymbol = product.symbol === 'ZTI' ? 'ZCX' : product.symbol

  const metaData = getProductByAddress(product.address)
  const data = indexCardStatisticsService(product)
  const priceHistoryWindow = product.priceHistory.map(buildTimeSeriesFromPriceHistory)
  const { navigationEvent } = useAnalyticsEventTracker(AnalyticsArea.Overview)
  const { setOverviewSectionComplete, overviewSectionComplete, stopTour, currentStep, startTour } =
    useTourGuide()
  React.useEffect(() => {
    setMarketCap(data.find((item) => item.title === 'Market cap')?.text.toString() || '')

    const proposedDelta =
      data.find((item) => item.title === '24H' || item.title === 'APY')?.text.toString() || ''
    setDelta(proposedDelta.includes('-') ? proposedDelta : `+${proposedDelta}`)
  }, [data])

  /**
   * IT: starts the tour
   * WHEN: not loading & Coming back from the product details page
   * WHY: NOTE: this condition is only met when coming back from the product details page, we need to start the tour again from the next (or previous in this case)
   */
  React.useEffect(() => {
    if (currentStep === 1 && overviewSectionComplete) {
      setOverviewSectionComplete(false)
      startTour()
    }
  }, [currentStep, overviewSectionComplete])

  const handleNavigation = (address: string) => {
    stopTour()
    setOverviewSectionComplete(true)
    navigationEvent(address)
  }

  return (
    <PerformanceItem>
      <ProductDetails>
        <Flex>
          <IconWrapper src={getAssetIconBySymbol(productSymbol.toUpperCase())} />
        </Flex>
        <ProductNameAndDescription>
          <div>
            <ProductTitle>{metaData.longName}</ProductTitle>
            {'  '}
            <ProductShortTitle>{metaData.symbol}</ProductShortTitle>
          </div>
          <TotalExposureDescription>{metaData.tagLine}</TotalExposureDescription>
        </ProductNameAndDescription>
      </ProductDetails>
      <MarketDetails>{marketCap}</MarketDetails>
      <ProductDelta isPositive={delta.includes('+')}>
        {delta.includes('+') ? delta.substring(1) : delta}%
      </ProductDelta>
      <MarketChart>
        {priceHistoryWindow.length !== 0 && (
          <PerformanceChartLite data={priceHistoryWindow.filter(byLastTwoWeeks).sort(byXAxis)} />
        )}
      </MarketChart>
      <MarketDetails>
        <InvestInProductButton
          id={`product-${productSymbol}`}
          buttonType="primary"
          onClick={() => handleNavigation(`${Path.PRODUCTS}/${productSymbol.toLowerCase()}`)}
        >
          Invest
        </InvestInProductButton>
      </MarketDetails>
    </PerformanceItem>
  )
}

export default ProductItem
